// Convite pendente da Gestão da equipe de pista (feature 161): o mesmo conteúdo no dialog da Home e no card.
// Visual da marca (violeta #7869DF/#4433B4 + verde #79CF62/#275030), entrada animada e números que contam.
import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, keyframes, useMediaQuery } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import GroupsIcon from '@mui/icons-material/Groups';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import TvIcon from '@mui/icons-material/Tv';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { animate, motion, useReducedMotion } from 'framer-motion';
import { __t } from '../../i18n/t';
import { OfferView } from '../../models/attendantGoalOffer';
import GradientButton, { noMotion } from './GradientButton';

const VIOLET = '#7869DF';
const VIOLET_DARK = '#4433B4';
const GREEN = '#79CF62';
const GREEN_DARK = '#275030';
const INK = '#232323';
const MUTED = '#5b6474';
const BADGE_GRADIENT = `linear-gradient(135deg, ${VIOLET}, #a855f7)`;
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(121,207,98,0.45); }
  50% { box-shadow: 0 0 0 8px rgba(121,207,98,0); }
`;
const borderFlow = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const list = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } } };
const item = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } } };

/** Conta de 0 até `value` (respeita "reduzir movimento"). */
const CountUp = ({ value }: { value: number }) => {
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(reduce ? value : 0);
  useEffect(() => {
    if (reduce) { setShown(value); return; }
    const controls = animate(0, value, { duration: 1.1, delay: 0.35, ease: EASE, onUpdate: (v) => setShown(Math.round(v)) });
    return () => controls.stop();
  }, [value, reduce]);
  return <>{shown}</>;
};

const fmtDay = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const HeaderBadge = ({ size = 28 }: { size?: number }) => (
  <Box sx={{ width: size, height: size, borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: BADGE_GRADIENT, boxShadow: '0 6px 16px rgba(120,105,223,0.35)' }}>
    <LeaderboardIcon sx={{ fontSize: size * 0.58, color: '#fff' }} />
  </Box>
);

// O dono não ativa com medo de cobrança automática: a oferta é grande e a garantia vem junto.
const FreeTile = () => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, sm: 1.75 }, borderRadius: 3, minWidth: 0,
    background: 'linear-gradient(135deg, #e9f8e4 0%, #d6f2cc 100%)', border: '1.5px solid rgba(121,207,98,0.55)',
    animation: `${glow} 2.6s ease-out infinite`, ...noMotion,
  }}>
    <Box sx={{ width: 46, height: 46, borderRadius: 2.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: GREEN, boxShadow: '0 6px 14px rgba(121,207,98,0.45)' }}>
      <EventAvailableIcon sx={{ fontSize: 26, color: '#fff' }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 800, lineHeight: 1.05, color: GREEN_DARK, letterSpacing: '-0.01em' }}>
        <CountUp value={60} /> {__t('dias grátis')}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#3d6b46', mt: 0.5, lineHeight: 1.3 }}>
        {__t('Sem custo e sem cobrança automática. No fim do teste, desliga sozinho.')}
      </Typography>
    </Box>
  </Box>
);

const StatTile = ({ icon: Icon, value, label, tone }: { icon: React.ElementType; value: number; label: string; tone: 'violet' | 'green' }) => {
  const color = tone === 'violet' ? VIOLET : '#3f9a2b';
  const bg = tone === 'violet' ? 'rgba(120,105,223,0.08)' : 'rgba(121,207,98,0.12)';
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 1.5 }, p: { xs: 1.25, sm: 1.75 }, borderRadius: 3, bgcolor: bg, border: `1px solid ${tone === 'violet' ? 'rgba(120,105,223,0.18)' : 'rgba(121,207,98,0.30)'}`, minWidth: 0 }}>
      <Box sx={{ width: { xs: 34, sm: 42 }, height: { xs: 34, sm: 42 }, borderRadius: 2.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', boxShadow: '0 4px 12px rgba(35,35,35,0.08)' }}>
        <Icon sx={{ fontSize: { xs: 20, sm: 24 }, color }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: { xs: 24, sm: 30 }, fontWeight: 800, lineHeight: 1, color: INK, fontVariantNumeric: 'tabular-nums' }}>
          <CountUp value={value} />
        </Typography>
        <Typography sx={{ fontSize: { xs: 11.5, sm: 13 }, color: MUTED, mt: 0.4, lineHeight: 1.25 }}>{label}</Typography>
      </Box>
    </Box>
  );
};

// Linhas curtas (até 20 palavras), sem falar de prêmio ou pagamento a frentista.
const STEPS: { icon: React.ElementType; text: string }[] = [
  { icon: AutoGraphIcon, text: 'Montamos metas de quantidade, fidelização e volume para cada posto, conforme os dados da pista' },
  { icon: TouchAppIcon, text: 'Você confere e ativa com 1 clique' },
  { icon: TvIcon, text: 'Acompanhe o ranking ao vivo no Modo TV e receba a análise da semana' },
];

const CtaButton = ({ disabled, onClick, grow }: { disabled: boolean; onClick: () => void; grow?: boolean }) => (
  <GradientButton disabled={disabled} onClick={onClick} endIcon={<ArrowForwardIcon />} grow={grow}>
    {__t('Ver agora grátis')}
  </GradientButton>
);

const DiscountPill = ({ sx }: { sx?: Record<string, unknown> }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 999, bgcolor: 'rgba(229,192,37,0.16)', color: '#7a5d00', alignSelf: 'flex-start', ...sx }}>
    <LocalOfferIcon sx={{ fontSize: 15 }} />
    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{__t('Para continuar depois, tem desconto especial')}</Typography>
  </Box>
);

/**
 * Barra do CTA no pé do dialog: fundo branco, sombra leve em cima e folga para a barra do iPhone.
 * (Componente à parte: o plugin de i18n embrulha em __t() todo literal dentro de `cond && <X sx=.../>`, inclusive valor de CSS.)
 */
const FooterBar = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{
    flexShrink: 0, position: 'relative', zIndex: 1, bgcolor: '#fff', px: 2, pt: 1.25,
    pb: 'calc(12px + env(safe-area-inset-bottom, 0px))', boxShadow: '0 -8px 20px rgba(35,35,35,0.12)',
  }}>
    {children}
  </Box>
);

// Celular ou janela baixa: o CTA vira uma barra fixa no pé do dialog (sempre visível). Senão fica logo depois dos números.
// 800 px de altura: com o dialog em 900 de largura (imagem de ~364 px), o CTA depois dos números termina ~680 px abaixo do topo do dialog.
const COMPACT_QUERY = '(max-width: 599.95px), (max-height: 799.95px)';

interface Props {
  offer: OfferView;
  headline: string;
  image: string | null;
  onImageError: () => void;
  busy: boolean;
  onSee: () => void;
  onDismiss: () => void;
  variant: 'dialog' | 'card';
}

const OfferPitch: React.FC<Props> = ({ offer, headline, image, onImageError, busy, onSee, onDismiss, variant }) => {
  const reduce = useReducedMotion();
  const compact = useMediaQuery(COMPACT_QUERY, { noSsr: true });
  const tail = offer.totals.tailAttendants;
  const attendants = tail > 0 ? tail : offer.totals.loyaltyLowAttendants;
  const attendantsLabel = tail > 0 ? __t('frentistas a acompanhar') : __t('frentistas a acompanhar na fidelização');
  const period = __t('Análise de %{inicio} a %{fim}', { inicio: fmtDay(offer.baselineStart), fim: fmtDay(offer.baselineEnd) });
  const isDialog = variant === 'dialog';
  const stickyActions = isDialog && compact;

  const actions = (sticky: boolean) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexWrap: sticky ? 'nowrap' : 'wrap' }}>
      <CtaButton disabled={busy} onClick={onSee} grow={sticky} />
      <Button disabled={busy} onClick={onDismiss} sx={{ textTransform: 'none', fontWeight: 600, color: MUTED, flexShrink: 0, '&:hover': { bgcolor: 'rgba(35,35,35,0.05)' } }}>
        {__t('Agora não')}
      </Button>
      {!sticky && <DiscountPill sx={{ ml: { sm: 'auto' } }} />}
    </Box>
  );

  const content = (
    <Box component={motion.div} variants={list} initial={reduce ? false : 'hidden'} animate="show"
      sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.75, sm: 2.25 }, minWidth: 0 }}>
      <Box component={motion.div} variants={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <HeaderBadge />
        <Typography sx={{ fontSize: 14, fontWeight: 800, background: BADGE_GRADIENT, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          {__t('Gestão da equipe de pista')}
        </Typography>
      </Box>

      <Box component={motion.div} variants={item}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: VIOLET, mb: 0.75 }}>
          {period}
        </Typography>
        {/* Texto da IA sempre como texto puro, nunca dangerouslySetInnerHTML. */}
        <Typography sx={{ fontSize: { xs: 19, sm: isDialog ? 25 : 21 }, fontWeight: 800, lineHeight: 1.25, color: INK, letterSpacing: '-0.01em' }}>
          {headline}
        </Typography>
      </Box>

      <Box component={motion.div} variants={item}
        sx={{ display: 'grid', gap: { xs: 1, sm: 1.5 }, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: '1fr 1fr 1.6fr' } }}>
        <StatTile icon={LocalGasStationIcon} value={offer.totals.stationsWithTrigger} label={__t('postos com oportunidade')} tone="violet" />
        <StatTile icon={GroupsIcon} value={attendants} label={attendantsLabel} tone="violet" />
        <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, display: 'grid' }}><FreeTile /></Box>
      </Box>

      {/* Card e dialog alto: CTA logo depois dos números. Dialog compacto: o CTA fica na barra fixa do pé. */}
      {!stickyActions && <Box component={motion.div} variants={item}>{actions(false)}</Box>}

      {isDialog && (
        <Box component={motion.div} variants={item}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED, mb: 1 }}>
            {__t('Como funciona')}
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' } }}>
            {STEPS.map(({ icon: Icon, text }, i) => (
              <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ position: 'relative', width: 36, height: 36, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(120,105,223,0.10)' }}>
                  <Icon sx={{ fontSize: 19, color: VIOLET }} />
                  <Box sx={{ position: 'absolute', top: -4, right: -4, width: 17, height: 17, borderRadius: '50%', bgcolor: VIOLET_DARK, color: '#fff', fontSize: 10.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {i + 1}
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK, lineHeight: 1.3 }}>{__t(text)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {stickyActions && <Box component={motion.div} variants={item}><DiscountPill /></Box>}
    </Box>
  );

  if (isDialog) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
        <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
          {image && (
            <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: GREEN }}>
              <Box component={motion.img} src={image} alt="" onError={onImageError}
                initial={reduce ? false : { scale: 1.08, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.1, ease: EASE }}
                sx={{ width: '100%', height: { xs: 96, sm: 'auto' }, aspectRatio: { xs: 'auto', sm: '2400 / 970' }, objectFit: 'cover', objectPosition: 'center top', display: 'block' }} />
            </Box>
          )}
          <Box sx={{
            position: 'relative', bgcolor: '#fff', p: { xs: 2, sm: 3.5 },
            ...(image && { mt: { xs: -2, sm: -3 }, borderRadius: { xs: '20px 20px 0 0', sm: '28px 28px 0 0' }, boxShadow: '0 -10px 30px rgba(35,35,35,0.10)' }),
          }}>
            {content}
          </Box>
        </Box>
        {stickyActions && <FooterBar>{actions(true)}</FooterBar>}
      </Box>
    );
  }

  // Card da Home: borda em gradiente animado, imagem em cima e o mesmo conteúdo, sem o "como funciona".
  return (
    <Box sx={{
      mt: 3, maxWidth: 920, p: '2px', borderRadius: 5,
      background: `linear-gradient(90deg, ${VIOLET}, ${GREEN}, #a855f7, ${VIOLET})`, backgroundSize: '200% 100%',
      animation: `${borderFlow} 6s linear infinite`, ...noMotion,
      boxShadow: '0 18px 40px rgba(68,51,180,0.14)',
    }}>
      <Box sx={{ borderRadius: 4.5, bgcolor: '#fff', overflow: 'hidden' }}>
        {image && (
          <Box component="img" src={image} alt="" onError={onImageError}
            sx={{ width: '100%', aspectRatio: '2400 / 970', objectFit: 'cover', objectPosition: 'center top', display: 'block', bgcolor: GREEN }} />
        )}
        <Box sx={{ p: { xs: 2.25, sm: 3 } }}>{content}</Box>
      </Box>
    </Box>
  );
};

export default OfferPitch;
